import { useCallback, useState } from 'react';
import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import ReviewRatingInput from './components/ReviewRatingInput';
import { AddReviewForm, ReviewAnswer } from './types';
import { useFormik } from 'formik';
import Typo from '../../../components/atom/Typo';
import colors from '../../../constants/colors.constants';
import strings from '../../../constants/strings.constants';
import Button from '../../../components/atom/Button';
import TextArea from '../../../components/atom/TextArea';
import Spinner from '../../../components/atom/Spinner';
import { closeWebPage } from '../../../utils/utils';
import moment from 'moment/moment';
import { getReservationSuccessLottieData } from '../../../utils/lottie';
import Lottie from 'react-lottie';
import {
  addReviewResponse,
  fetchReviewForm,
  fetchReviewResponseAvailable,
} from '../../../apis/review-v2';
import { ReviewForm } from '../../../apis/models/review/res/ReviewForm';
import { ReviewResponse } from '../../../apis/models/review/req/AddReviewResponseBody';
import AppError from '../../../apis/models/common/res/AppError';
import useAlertStore from '../../../store/useAlertStore';
import { ReviewResponseType } from '../../../apis/models/review/res/ReviewQuestion';
import Checkbox from '../../../components/atom/Checkbox';
import { Helmet } from 'react-helmet';
import ReviewRatingWithTextInput from './components/ReviewRatingWithTextInput';

function AddReviewV2Page() {
  const INITIAL_VALUES: AddReviewForm = {};
  const formik = useFormik<AddReviewForm>({
    initialValues: INITIAL_VALUES,
    onSubmit: async (values) => {
      await requestAddReview(values);
    },
    enableReinitialize: true,
    validateOnMount: true,
  });

  const [searchParams] = useSearchParams();
  const reservationId = searchParams.get('reservationId');
  const langCode = searchParams.get('lang');
  const [noReviewConfig, setNoReviewConfig] = useState<boolean>(false);
  const [completedReview, setCompletedReview] = useState<boolean>(false);
  const [reviewForm, setReviewForm] = useState<ReviewForm>();
  const [submitting, setSubmitting] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const { showAlert } = useAlertStore();
  const [partnerName, setPartnerName] = useState('');

  const { isLoading: fetchingReviewConfig } = useQuery(
    'FETCH_REVIEW_CONFIG',
    () => fetchReviewForm(reservationId as string),
    {
      enabled: !!reservationId,
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
      refetchOnReconnect: false,
      cacheTime: 0,
      retry: false,
      onSuccess: (data) => {
        const { partner } = data;

        if (!langCode) {
          setReviewForm(data);
        }
        // 번역
        else {
          const {
            questions,
            introMessage,
            eventContents,
            globalReviewTemplates = [],
          } = data;

          let translatedIntroMessage = introMessage;
          let translatedEventContents = eventContents;

          const matchedTemplate = globalReviewTemplates.find(({ language }) => {
            return language.code === langCode;
          });
          if (matchedTemplate) {
            const { introMessage, eventContents } = matchedTemplate;
            translatedIntroMessage = introMessage;
            translatedEventContents = eventContents;
          }

          const translatedData = {
            ...data,
            introMessage: translatedIntroMessage,
            eventContents: translatedEventContents,
            questions: questions.map((question) => {
              const { globalReviewQuestions = [] } = question;
              const matchedGlobalReviewQuestion = globalReviewQuestions.find(
                ({ language }) => {
                  return language.code === langCode;
                },
              );
              if (matchedGlobalReviewQuestion) {
                return {
                  ...question,
                  title: matchedGlobalReviewQuestion.title,
                  responseOptions: matchedGlobalReviewQuestion.responseOptions,
                };
              } else {
                return question;
              }
            }),
          };

          setReviewForm(translatedData);
        }

        if (partner) {
          const { partnerName, displayName } = partner;
          setPartnerName(displayName || partnerName);
        }
      },
      onError: () => {
        setNoReviewConfig(true);
      },
    },
  );

  const { isLoading: fetchingReview } = useQuery(
    'FETCH_REVIEW',
    () => fetchReviewResponseAvailable(reservationId as string),
    {
      enabled: !!reservationId,
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
      refetchOnReconnect: false,
      cacheTime: 0,
      retry: false,
      onError: () => {
        setCompletedReview(true);
      },
    },
  );

  const isFetching = useCallback(() => {
    return fetchingReview || fetchingReviewConfig;
  }, [fetchingReview, fetchingReviewConfig]);

  const requestAddReview = async (values: AddReviewForm) => {
    setSubmitting(true);
    try {
      if (reservationId && reviewForm) {
        const { partner, questions } = reviewForm;
        const { partnerCode } = partner;
        const canChooseAnonymous = canChooseAnonymousReview(reviewForm);

        const reviewResponses: ReviewResponse[] = [];
        Object.keys(values).forEach((key) => {
          const questionId = parseInt(key);
          const question = questions.find(({ id }) => id === questionId);
          if (question) {
            const answer = values[key];
            // 주관식 답변
            if (typeof answer === 'string' && answer) {
              reviewResponses.push({
                questionId,
                content: answer,
              });
            }

            // 복수 선택 문항
            if (Array.isArray(answer)) {
              reviewResponses.push({
                questionId,
                content: answer.join(','),
              });
            }

            // 별점 답변
            if (typeof answer === 'number') {
              reviewResponses.push({
                questionId,
                rate: answer,
              });
            }

            // 별점 + 주관식 답변
            if (typeof answer === 'object' && 'rating' in answer) {
              reviewResponses.push({
                questionId,
                rate: answer.rating,
                content: answer.comment,
              });
            }
          }
        });

        await addReviewResponse(partnerCode, {
          responses: reviewResponses,
          reservationId,
          isAnonymous: canChooseAnonymous ? isAnonymous : false,
        });

        setCompletedReview(true);
      }
    } catch (e) {
      const error = e as AppError;
      if (error.message) {
        showAlert(error.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const updateFormValue = (questionId: string, value: ReviewAnswer) => {
    formik.setFieldValue(questionId, value);
  };
  const canSubmit = () => {
    if (reviewForm) {
      const { questions } = reviewForm;

      const requiredQuestionIds = questions.filter(({ responseType }) => {
        return (
          responseType === ReviewResponseType.SCORE ||
          responseType === ReviewResponseType.SCORE_TEXT
        );
      });

      return (
        requiredQuestionIds.length ===
        Object.keys(formik.values).filter((key) => {
          if (typeof formik.values[key] === 'object') {
            return (
              (
                formik.values[key] as {
                  rating: number;
                  comment: string;
                }
              ).rating > 0
            );
          }
          return typeof formik.values[key] === 'number';
        }).length
      );
    }
    return false;
  };

  if (!reservationId) {
    return (
      <Typo type="m-16" color={colors.subTitle}>
        noReservationId 요청이 만료되었거나, 올바른 접근 경로가 아닙니다.
      </Typo>
    );
  }

  if (isFetching()) {
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (noReviewConfig) {
    return (
      <Typo type="m-16" color={colors.subTitle}>
        요청이 만료되었거나, 올바른 접근 경로가 아닙니다.
      </Typo>
    );
  }

  if (completedReview && reviewForm) {
    const lottieJson = getReservationSuccessLottieData(colors.primary3);
    return (
      <div className="h-full flex flex-col">
        <div
          className="h-full flex-1"
          style={{
            padding: '0px 20px',
          }}
        >
          <div className="flex flex-col justify-center text-center h-full">
            <Lottie
              options={{
                autoplay: true,
                animationData: lottieJson,
                loop: false,
              }}
              width={68}
              height={68}
            />
            <div className="mt-2">
              <Typo type="b-24">{strings.completeReview}</Typo>
            </div>
            <div className="mt-2">
              <Typo
                type="r-16"
                color={colors.subTitle}
                style={{
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.5,
                }}
              >
                {strings.completeReviewDescription.replace(
                  '{partnerName}',
                  partnerName,
                )}
              </Typo>
            </div>
          </div>
        </div>
        <div className="p-[20px]">
          <Button
            style={{
              width: '100%',
            }}
            onClick={() => {
              closeWebPage();
            }}
          >
            {strings.confirm}
          </Button>
        </div>
      </div>
    );
  }

  if (reviewForm) {
    const { questions, reservation, introMessage, eventContents } = reviewForm;
    const canChooseAnonymous = canChooseAnonymousReview(reviewForm);
    const {
      reservedAt,
      partnerEmployee,
      consultingEmployee,
      treatmentCategoryName,
      treatmentSubCategoryName,
    } = reservation;
    const reservedAtText = moment(reservedAt).format('YY.MM.DD (dd) HH:mm');
    let treatmentCategoryText = '';
    let doctorName = '';
    let doctorImage = '';
    let consultantName = '';
    let consultantImage = '';

    if (treatmentCategoryName && treatmentSubCategoryName) {
      treatmentCategoryText = `${treatmentCategoryName} / ${treatmentSubCategoryName}`;
    } else if (treatmentSubCategoryName) {
      treatmentCategoryText = treatmentSubCategoryName;
    } else if (treatmentCategoryName) {
      treatmentCategoryText = treatmentCategoryName;
    }

    if (partnerEmployee) {
      const { partnerEmployeeName, partnerEmployeeProfileImageUrl } =
        partnerEmployee;
      doctorName = partnerEmployeeName;
      doctorImage = partnerEmployeeProfileImageUrl || '';
    }

    if (consultingEmployee) {
      const { partnerEmployeeName, partnerEmployeeProfileImageUrl } =
        consultingEmployee;
      consultantName = partnerEmployeeName;
      consultantImage = partnerEmployeeProfileImageUrl || '';
    }

    const isCheckboxSelected = (questionId: number, optionText: string) => {
      const values = (formik.values[`${questionId}`] as string[]) || [];
      if (Array.isArray(formik.values[`${questionId}`])) {
        return values.includes(optionText);
      }

      return false;
    };

    return (
      <div className="flex flex-col h-full">
        <Helmet>
          <title>리뷰</title>
        </Helmet>
        <div
          className="min-h-[48px] flex items-center justify-center"
          style={{
            borderBottom: `1px solid ${colors.defaultBorder}`,
          }}
        >
          <Typo type="b-16">{strings.addReview}</Typo>
        </div>
        <div
          className="flex flex-col gap-4 grow overflow-auto"
          style={{
            background: colors.greyHover,
          }}
        >
          <div className="p-[20px] bg-white">
            {introMessage && (
              <div
                className="mb-5"
                style={{
                  lineHeight: 1.3,
                  fontWeight: 500,
                }}
                dangerouslySetInnerHTML={{
                  __html: introMessage,
                }}
              />
            )}
            <div
              className="flex flex-col gap-2"
              style={{
                borderRadius: 12,
                padding: '14px 16px',
                background: colors.greyHover,
              }}
            >
              <div>
                <Typo type="b-18">{`${strings.visitDate} ${reservedAtText}`}</Typo>
              </div>

              <div>
                <Typo type="r-16">{partnerName}</Typo>
              </div>
            </div>
          </div>

          {eventContents && (
            <div className="bg-white p-[20px]">
              <div
                className="mt-5"
                style={{
                  lineHeight: 1.2,
                }}
                dangerouslySetInnerHTML={{
                  __html: eventContents,
                }}
              />
            </div>
          )}

          <div className="flex flex-col gap-10 bg-white p-[20px]">
            {questions.map(
              ({ title, responseType, responseOptions = [], id, targetBy }) => {
                const formKey = `${id}`;

                if (responseType === ReviewResponseType.TEXT) {
                  return (
                    <div className="flex flex-col gap-3" key={id}>
                      <div className="flex justify-center text-left">
                        <Typo
                          type="m-16"
                          style={{
                            lineHeight: 1.3,
                          }}
                        >
                          {title}
                        </Typo>
                      </div>
                      <TextArea
                        placeholder={strings.reviewCommentPlaceholder}
                        value={formik.values[formKey] as string}
                        onChange={(value) => {
                          updateFormValue(formKey, value);
                        }}
                      />
                    </div>
                  );
                }

                if (
                  responseType === ReviewResponseType.CHECKBOX &&
                  responseOptions.length > 0
                ) {
                  return (
                    <div className="flex flex-col gap-3" key={id}>
                      <div className="flex justify-center text-left">
                        <Typo
                          type="m-16"
                          style={{
                            lineHeight: 1.3,
                          }}
                        >
                          {title}
                        </Typo>
                      </div>
                      {responseOptions.map((optionText) => {
                        const checked = isCheckboxSelected(id, optionText);
                        return (
                          <div
                            key={optionText}
                            className="flex items-center gap-2"
                            onClick={() => {
                              let prevValues: string[] = [];
                              if (Array.isArray(formik.values[formKey])) {
                                prevValues = formik.values[formKey] as string[];
                              }
                              if (checked) {
                                updateFormValue(
                                  formKey,
                                  prevValues.filter(
                                    (value) => value !== optionText,
                                  ),
                                );
                              } else {
                                prevValues.push(optionText);
                                updateFormValue(formKey, prevValues);
                              }
                            }}
                          >
                            <Checkbox id={optionText} checked={checked} />
                            <Typo type="r-16">{optionText}</Typo>
                          </div>
                        );
                      })}
                    </div>
                  );
                }

                let profileInfo:
                  | {
                      name: string;
                      imageUrl?: string;
                      treatmentCategory?: string;
                    }
                  | undefined;

                if (targetBy === 'CONSULTANT' && consultantName) {
                  profileInfo = {
                    name: consultantName,
                    imageUrl: consultantImage,
                    treatmentCategory: treatmentCategoryText,
                  };
                }

                if (targetBy === 'DOCTOR' && doctorName) {
                  profileInfo = {
                    name: doctorName,
                    imageUrl: doctorImage,
                    treatmentCategory: treatmentCategoryText,
                  };
                }

                if (responseType === ReviewResponseType.SCORE_TEXT) {
                  return (
                    <ReviewRatingWithTextInput
                      profileInfo={profileInfo}
                      key={id}
                      label={title}
                      value={
                        formik.values[formKey] as {
                          rating: number;
                          comment: string;
                        }
                      }
                      onChange={(value) => {
                        updateFormValue(formKey, value);
                      }}
                    />
                  );
                }
                return (
                  <ReviewRatingInput
                    profileInfo={profileInfo}
                    key={id}
                    label={title}
                    value={formik.values[formKey] as number}
                    onChange={(value) => {
                      updateFormValue(formKey, value);
                    }}
                  />
                );
              },
            )}
            {canChooseAnonymous && (
              <div
                className="flex items-center gap-2"
                onClick={() => {
                  setIsAnonymous(!isAnonymous);
                }}
              >
                <Checkbox id={strings.writeAnonymously} checked={isAnonymous} />
                <Typo type="r-16">{strings.writeAnonymously}</Typo>
              </div>
            )}
          </div>
        </div>
        <div
          className="shrink-0 p-[20px]"
          style={{
            padding: '16px 20px',
            boxShadow: '0px -4px 30px 0px #0000001f',
          }}
        >
          <Button
            style={{
              width: '100%',
            }}
            disabled={submitting || !canSubmit()}
            onClick={() => {
              formik.handleSubmit();
            }}
          >
            {strings.addReview}
          </Button>
        </div>
      </div>
    );
  }

  return <div />;
}

const canChooseAnonymousReview = (reviewForm: ReviewForm) => {
  return reviewForm.useAnonymousReview !== false;
};

export default AddReviewV2Page;
